import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { LearnItemMetaDataComponentMode } from '../../lesson/learnitem-metadata/learnitem-metadata.component';
import { Store } from '@ngxs/store';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { GenericTreeEntity } from '../generic-tree.entity';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { ConsumerEntityService, ConsumerEntityState } from '../../consumer/services/consumer.entity.service';
import { GlobalMetaDataEntityService, GlobalMetaDataEntityState } from '../../global-metadata/services/global-metadata.entity.service';
import { GlobalMetaDataEntity } from '../../global-metadata/global-metadata.entity';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'pixl-metadata-assignment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslocoModule
  ],
  templateUrl: './metadata-assignment-dialog.component.html',
  styleUrl: './metadata-assignment-dialog.component.scss'
})
export class MetadataAssignmentDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<MetadataAssignmentDialogComponent>);
  public data = inject(MAT_DIALOG_DATA) as { node: GenericTreeEntity };
  private store = inject(Store);
  private fb = inject(FormBuilder);
  private globalMetadataService = inject(GlobalMetaDataEntityService);

  private consumerService = inject(ConsumerEntityService);

  form = this.fb.group({});
  consumer = signal<ConsumerEntity | undefined>(undefined);
  mode = LearnItemMetaDataComponentMode.GLOBAL;

  allMetadata = signal<GlobalMetaDataEntity[]>([]);
  activeMetadata = signal<GlobalMetaDataEntity[]>([]);
  adaptedActiveMetadata = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.activeMetadata().map((m) => ({ ...m, fieldConf: m.fieldConfig } as any));
  });

  async ngOnInit(): Promise<void> {
    if (this.data.node.consumerId) {
      await this.consumerService.ensureSingleLoaded(this.data.node.consumerId);
      const consumer = this.store.selectSnapshot(ConsumerEntityState.byId)(this.data.node.consumerId);
      this.consumer.set(consumer);
    }
    this.loadMetadata();
  }

  async loadMetadata() {
    await this.globalMetadataService.ensureLoaded({ page: { size: 1000 } });

    const entities = this.store.selectSnapshot(GlobalMetaDataEntityState.all);

    const models = [...entities];
    models.sort((a, b) => a.name.localeCompare(b.name));

    this.allMetadata.set(models);
    this.initActiveMetadata();
  }

  initActiveMetadata() {
    const currentData = this.data.node.consumerData || {};
    const active: GlobalMetaDataEntity[] = [];

    this.allMetadata().forEach(m => {
      // If any field of this metadata definition is present in the node's data, consider it active.
      const hasData = m.fieldConfig?.some(f => Object.prototype.hasOwnProperty.call(currentData, f.name));
      if (hasData) {
        active.push(m);
      }
    });

    this.activeMetadata.set(active);
  }

  onSave(): void {
    const consumerData = this.form.get('consumerData')?.value || {};

    this.dialogRef.close({
      ...this.data.node,
      consumerData: {
        ...this.data.node.consumerData,
        ...consumerData
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
